import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

/* Abstract, clean, CSS-built product mockups. No stock photos.
   Rendered on a dark glass surface so they look like real product UI. */

/* lightweight looping typewriter */
function useTypewriter(lines, speed = 38, hold = 1100) {
  const [text, setText] = useState("");
  const [li, setLi] = useState(0);
  useEffect(() => {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setText(lines[0]);
      return undefined;
    }
    let i = 0;
    let timer;
    const line = lines[li % lines.length];
    const tick = () => {
      i += 1;
      setText(line.slice(0, i));
      if (i < line.length) {
        timer = setTimeout(tick, speed);
      } else {
        timer = setTimeout(() => setLi((v) => v + 1), hold);
      }
    };
    setText("");
    timer = setTimeout(tick, speed);
    return () => clearTimeout(timer);
  }, [li, lines, speed, hold]);
  return text;
}

const Bar = ({ h, active }) => (
  <div
    className="w-full rounded-md"
    style={{ height: `${h}%`, background: active ? "#E4FF00" : "rgba(255,255,255,0.14)" }}
  />
);

const Window = ({ children, title }) => (
  <div className="h-full w-full overflow-hidden rounded-[18px] border border-white/10 bg-[#0a0a0a]">
    <div className="flex items-center gap-1.5 border-b border-white/10 px-4 py-3">
      <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
      <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#E4FF00]" />
      {title ? (
        <span className="ml-3 text-[10px] uppercase tracking-[0.25em] text-white/40">
          {title}
        </span>
      ) : null}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

function Dashboard() {
  const bars = [40, 62, 48, 78, 55, 90, 70, 84, 60, 96, 72, 88];
  return (
    <Window title="Revenue">
      <div className="grid grid-cols-3 gap-3">
        {["MRR", "Net", "Churn"].map((k, i) => (
          <div key={k} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
            <div className="text-[9px] uppercase tracking-widest text-white/40">{k}</div>
            <div className="mt-1 text-lg font-semibold text-white">
              {["$1.2M", "$884K", "2.1%"][i]}
            </div>
            <div className="text-[10px] text-[#E4FF00]">+{[12, 8, 0][i]}.{i + 2}%</div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex h-28 items-end gap-1.5 rounded-xl border border-white/10 bg-white/[0.02] p-3">
        {bars.map((h, i) => (
          <Bar key={i} h={h} active={i === bars.length - 3} i={i} />
        ))}
      </div>
    </Window>
  );
}

function Analytics() {
  return (
    <Window title="Analytics">
      <svg viewBox="0 0 300 120" className="w-full">
        <defs>
          <linearGradient id="g1" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#E4FF00" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#E4FF00" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M0 90 C 40 70, 60 40, 100 50 S 160 30, 200 38 S 260 10, 300 20 L300 120 L0 120 Z"
          fill="url(#g1)"
        />
        <path
          d="M0 90 C 40 70, 60 40, 100 50 S 160 30, 200 38 S 260 10, 300 20"
          fill="none"
          stroke="#E4FF00"
          strokeWidth="2"
        />
      </svg>
      <div className="mt-3 grid grid-cols-4 gap-2">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="rounded-lg border border-white/10 bg-white/[0.03] p-2">
            <div className="h-1.5 w-8 rounded bg-white/20" />
            <div className="mt-2 h-2 w-12 rounded bg-white/30" />
          </div>
        ))}
      </div>
    </Window>
  );
}

function Table() {
  return (
    <Window title="Records">
      <div className="space-y-2">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5"
          >
            <span
              className="h-6 w-6 rounded-md"
              style={{ background: i % 3 === 0 ? "#E4FF00" : "rgba(255,255,255,0.12)" }}
            />
            <span className="h-2 flex-1 rounded bg-white/15" />
            <span className="h-2 w-12 rounded bg-white/10" />
            <span className="h-2 w-8 rounded bg-[#E4FF00]/60" />
          </div>
        ))}
      </div>
    </Window>
  );
}

function AISystem() {
  const typed = useTypewriter(
    [
      "> enrich --accounts 50 --region DACH",
      "> score --signal hiring,funding",
      "> draft --persona founder --tone direct",
      "> route --reply warm --to human",
    ],
    34,
    1200
  );
  return (
    <Window title="Agent">
      <div className="font-mono text-[11px] leading-relaxed">
        <div className="text-white/40">parabolum@leadforge ~ %</div>
        <div className="mt-1 min-h-[18px] text-[#E4FF00]">
          {typed}
          <motion.span
            animate={{ opacity: [1, 0, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
            className="ml-0.5 inline-block"
          >
            _
          </motion.span>
        </div>
        <div className="mt-3 space-y-1.5 text-white/55">
          <div>✓ 50 accounts enriched</div>
          <div>✓ intent scored — 31 hot</div>
          <div>✓ 50 messages drafted</div>
        </div>
        <div className="mt-3 flex gap-2">
          {["Enrich", "Score", "Draft", "Route"].map((s) => (
            <span
              key={s}
              className="rounded-full border border-white/10 px-2.5 py-1 text-[9px] uppercase tracking-wider"
              style={{ color: "#0a0a0a", background: "#E4FF00" }}
            >
              {s}
            </span>
          ))}
        </div>
      </div>
    </Window>
  );
}

function Pipeline() {
  const cols = ["New", "Enriched", "Engaged", "Won"];
  return (
    <Window title="Pipeline">
      <div className="grid grid-cols-4 gap-2">
        {cols.map((c, ci) => (
          <div key={c} className="space-y-2">
            <div className="text-[9px] uppercase tracking-wider text-white/40">{c}</div>
            {[...Array(3 - (ci % 2))].map((_, i) => (
              <div
                key={i}
                className="rounded-lg border border-white/10 bg-white/[0.03] p-2"
                style={ci === 3 && i === 0 ? { borderColor: "#E4FF00" } : {}}
              >
                <div className="h-1.5 w-full rounded bg-white/20" />
                <div className="mt-1.5 h-1.5 w-2/3 rounded bg-white/10" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </Window>
  );
}

function Trading() {
  const bars = [30, 45, 38, 60, 52, 72, 64, 85, 70, 92, 80, 68, 78, 58];
  return (
    <Window title="BTC / USD">
      <div className="flex items-baseline justify-between">
        <div className="text-xl font-semibold text-white">$67,420</div>
        <div className="text-xs text-[#E4FF00]">+4.21%</div>
      </div>
      <div className="mt-3 flex h-24 items-end gap-1">
        {bars.map((h, i) => (
          <div
            key={i}
            className="w-full rounded-sm"
            style={{ height: `${h}%`, background: h > 75 ? "#E4FF00" : "rgba(255,255,255,0.16)" }}
          />
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-lg border border-[#E4FF00]/30 bg-[#E4FF00]/10 py-2 text-center text-[11px] font-semibold text-[#E4FF00]">
          BUY
        </div>
        <div className="rounded-lg border border-white/10 py-2 text-center text-[11px] font-semibold text-white/60">
          SELL
        </div>
      </div>
    </Window>
  );
}

function Booking() {
  return (
    <Window title="Booking">
      <div className="grid grid-cols-7 gap-1.5">
        {[...Array(21)].map((_, i) => (
          <div
            key={i}
            className="aspect-square rounded-md"
            style={{
              background: [4, 9, 12, 17].includes(i)
                ? "#E4FF00"
                : "rgba(255,255,255,0.08)",
            }}
          />
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
        <div>
          <div className="h-2 w-20 rounded bg-white/25" />
          <div className="mt-1.5 h-2 w-12 rounded bg-white/10" />
        </div>
        <span className="rounded-full bg-[#E4FF00] px-3 py-1 text-[10px] font-bold text-black">
          Confirm
        </span>
      </div>
    </Window>
  );
}

function Shop() {
  return (
    <Window title="Parts">
      <div className="grid grid-cols-3 gap-2">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-2">
            <div
              className="aspect-square rounded-lg"
              style={{
                background:
                  i % 4 === 0
                    ? "linear-gradient(135deg,#E4FF00,#9bb000)"
                    : "rgba(255,255,255,0.07)",
              }}
            />
            <div className="mt-2 h-1.5 w-full rounded bg-white/20" />
            <div className="mt-1 h-1.5 w-1/2 rounded bg-[#E4FF00]/60" />
          </div>
        ))}
      </div>
    </Window>
  );
}

function Garage() {
  const rows = [
    { p: "Downpipe 3″ — Stainless", f: "BMW M3 G80", s: 12 },
    { p: "Forged Wheel 19″ — Set", f: "Audi RS3 8Y", s: 4 },
    { p: "Performance Clutch Kit", f: "Golf R MK8", s: 7 },
    { p: "Catback Exhaust — Ti", f: "Porsche 992", s: 2 },
    { p: "Brake Pads — Track", f: "Universal", s: 36 },
  ];
  return (
    <Window title="REVIX · Admin">
      <div className="grid grid-cols-12 items-center gap-2 border-b border-white/10 pb-2 text-[9px] uppercase tracking-[0.18em] text-white/40">
        <div className="col-span-5">Part</div>
        <div className="col-span-4">Fitment</div>
        <div className="col-span-3 text-right">Stock</div>
      </div>
      {rows.map((r, i) => (
        <div
          key={i}
          className="grid grid-cols-12 items-center gap-2 border-b border-white/[0.05] py-2 text-[11px]"
        >
          <div className="col-span-5 truncate text-white/85">{r.p}</div>
          <div className="col-span-4 truncate text-white/55">{r.f}</div>
          <div className="col-span-3 flex items-center justify-end gap-2">
            <span className="text-[11px] font-semibold text-white">{r.s}</span>
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                r.s > 5 ? "bg-[#E4FF00]" : r.s > 0 ? "bg-yellow-400/80" : "bg-red-400/80"
              }`}
            />
          </div>
        </div>
      ))}
      <div className="mt-3 flex items-center justify-between text-[9px] uppercase tracking-[0.22em] text-white/35">
        <span>Catalogue · Orders · Customers</span>
        <span className="rounded-full border border-white/10 px-2 py-0.5 text-white/50">
          Live
        </span>
      </div>
    </Window>
  );
}

function Website() {
  return (
    <Window title="parabolum.studio">
      {/* faux nav row */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-[#E4FF00]" />
          <span className="text-[10px] font-semibold tracking-tight text-white">
            STUDIO
          </span>
        </div>
        <div className="hidden gap-4 sm:flex">
          {["Work", "Tech", "About"].map((l) => (
            <span key={l} className="text-[9px] tracking-wide text-white/40">
              {l}
            </span>
          ))}
        </div>
        <span className="rounded-full bg-white px-2 py-0.5 text-[9px] font-semibold text-black">
          Talk
        </span>
      </div>

      {/* hero */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div className="text-[9px] uppercase tracking-[0.25em] text-white/35">
          A boutique studio
        </div>
        <div className="mt-2 font-extrabold leading-[0.95] tracking-tight text-white" style={{ fontSize: "clamp(18px, 2.4vw, 26px)" }}>
          Built to launch.
          <br />
          Built to last.
        </div>
        <div className="mt-2.5 max-w-[80%] text-[10px] leading-snug text-white/45">
          Editorial sites engineered for speed, story and conversion.
        </div>
        <div className="mt-3 flex items-center gap-2">
          <span className="rounded-full bg-[#E4FF00] px-2.5 py-1 text-[9px] font-semibold text-black">
            See work
          </span>
          <span className="rounded-full border border-white/15 px-2.5 py-1 text-[9px] font-medium text-white/70">
            Start project
          </span>
        </div>
      </div>

      {/* cards row */}
      <div className="mt-3 grid grid-cols-3 gap-2">
        {["Editorial", "Fast", "Memorable"].map((l, i) => (
          <div
            key={l}
            className="rounded-lg border border-white/10 bg-white/[0.03] p-2"
          >
            <div className="text-[8px] uppercase tracking-widest text-white/35">
              0{i + 1}
            </div>
            <div className="mt-1 text-[10px] font-semibold text-white">{l}</div>
          </div>
        ))}
      </div>
    </Window>
  );
}

const MAP = {
  dashboard: Dashboard,
  analytics: Analytics,
  table: Table,
  ai: AISystem,
  pipeline: Pipeline,
  trading: Trading,
  booking: Booking,
  shop: Shop,
  website: Website,
  garage: Garage,
};

export default function UIMock({ kind = "dashboard", className = "" }) {
  const Cmp = MAP[kind] || Dashboard;
  return (
    <div className={`h-full w-full ${className}`}>
      <Cmp />
    </div>
  );
}
